import {
  ProductDetailsFragment,
  ProductVariantDetailsWihoutProductFragment
} from '../saleor-graphql';

export const variantName = (variant: {
  name?: string | null;
  sku?: string | null;
  product?: { name?: string | null };
}) => {
  if (variant.name && variant.name == variant.sku) {
    return `${variant.product?.name ? variant.product.name : variant.sku}`;
  }

  if (variant.product?.name && variant.product.name == variant.name) {
    return `${variant.name}`;
  }

  return variant.product?.name ? `${variant.product?.name} (${variant.name})` : `${variant.name}`;
};

/**
 * Minimal type for variant display name calculation
 */
type VariantWithProduct = {
  name: string;
  sku?: string | null;
  product: {
    name: string;
    productVariants?: {
      totalCount?: number | null;
    } | null;
  };
};

/**
 * Generate the display name for a variant.
 * Logic:
 * - If variant name is different from SKU:
 *   - If only 1 variant: use product name
 *   - If more than 1 variant: use "product name - variant name"
 * - Otherwise: just use product name
 */
export function getVariantDisplayName(variant: VariantWithProduct): string {
  const productName = variant.product.name;
  const variantName = variant.name;
  const variantSku = variant.sku;
  const productVariantsCount = variant.product.productVariants?.totalCount ?? 0;

  // Check if variant name is different from SKU
  const isVariantNameDifferentFromSku = variantName !== variantSku;

  if (isVariantNameDifferentFromSku) {
    if (productVariantsCount <= 1) {
      return productName;
    } else {
      return `${productName} - ${variantName}`;
    }
  } else {
    return productName;
  }
}

/**
 * Minimal type for variant URL generation
 */
type VariantWithId = {
  id: string;
  product: {
    productVariants?: {
      totalCount?: number | null;
    } | null;
  };
};

/**
 * Generate the correct URL for a product variant
 * - If product has only 1 variant: link directly to product page (no query param)
 * - If product has multiple variants: include variant query param
 */
export function getVariantUrl(
  variant: VariantWithId,
  baseUrl: string
): string {
  if (!variant.product) {
    return baseUrl;
  }

  const productVariantsCount = variant.product.productVariants?.totalCount ?? 0;

  if (productVariantsCount <= 1) {
    return baseUrl;
  }

  return `${baseUrl}?variant=${variant.id}`;
}

export const getDefaultVariant = (product: ProductDetailsFragment) => {
  return (product.variants || []).find((variant) => variant.id == product.defaultVariant?.id);
};

export const variantWithProduct = (
  variant: ProductVariantDetailsWihoutProductFragment,
  product: ProductDetailsFragment
) => {
  return {
    ...variant,
    product,
  };
};